import { useCallback, useEffect } from 'react';
import { CreateShippingRecord, Vehicle, Driver } from '../types';
import { loadUnloadFeeApi, tollFeeApi, shippingPriceByCapacityApi, rm2FeeApi, driverApi } from '../services/api';

interface UseShippingFeeCalculationsProps {
  formData: CreateShippingRecord;
  setFormData: React.Dispatch<React.SetStateAction<CreateShippingRecord>>;
  vehicles: Vehicle[];
  drivers: Driver[];
  isEditMode: boolean;
}

export const useShippingFeeCalculations = ({
  formData,
  setFormData,
  vehicles,
  drivers,
  isEditMode
}: UseShippingFeeCalculationsProps) => {
  
  const calculateFees = useCallback(async (licensePlate: string, provinceId?: number, districtId?: number) => {
    try {
      const vehicle = vehicles.find(v => v.licensePlate && v.licensePlate.toLowerCase() === licensePlate.toLowerCase());
      if (!vehicle || !vehicle.capacityTonsId) return;
      
      try {
        const loadUnloadResponse = await loadUnloadFeeApi.getByCapacity(vehicle.capacityTonsId);
        setFormData(prev => ({ ...prev, loadUnloadFee: loadUnloadResponse.data.fee }));
      } catch (error) { }
      
      try {
        let tollFee = 0;
        const tollFees = await tollFeeApi.getAll();
        let found: any = null;
        if (districtId) {
          found = tollFees.data.find((t: any) => t.capacityTonsId === vehicle.capacityTonsId && t.districtId === districtId);
          if (!found && provinceId) {
            found = tollFees.data.find((t: any) => t.capacityTonsId === vehicle.capacityTonsId && t.provinceId === provinceId && !t.districtId);
          }
        } else if (provinceId) {
          found = tollFees.data.find((t: any) => t.capacityTonsId === vehicle.capacityTonsId && t.provinceId === provinceId && !t.districtId);
        }
        if (found) tollFee = found.fee;
        setFormData(prev => ({ ...prev, tollFee }));
      } catch (error) { }
    
      // Use capacity-based pricing API
      try {
        const shippingPriceResponse = await shippingPriceByCapacityApi.calculatePrice({
          districtId,
          provinceId,
          capacityTonsId: vehicle.capacityTonsId
        });
        setFormData(prev => ({ ...prev, shippingCost: shippingPriceResponse.data }));
      } catch (error) { 
        console.error('Error calculating shipping price by capacity:', error);
      }
    } catch (error) { }
  }, [vehicles, setFormData]);

  const calculateAllCosts = useCallback(async () => {
    try {
      const vehicle = vehicles.find(v => v.licensePlate && v.licensePlate.toLowerCase() === formData.licensePlate.toLowerCase());
      if (!vehicle || !vehicle.capacityTonsId) {
        alert('Please select a valid vehicle first');
        return;
      }

      // Find preferred driver for this vehicle
      const preferredDriver = drivers.find(d => 
        Array.isArray(d.preferredVehicleIds) && 
        d.preferredVehicleIds.includes(vehicle.id)
      );

      // Fetch all fees in parallel - using capacity-based shipping price API
      const [loadUnloadResponse, tollFeesResponse, shippingPriceResponse, rm2FeeResponse, salaryRateResponse] = await Promise.allSettled([
        loadUnloadFeeApi.getByCapacity(vehicle.capacityTonsId),
        tollFeeApi.getAll(),
        // Use the new capacity-based pricing API
        shippingPriceByCapacityApi.calculatePrice({
          districtId: formData.districtId,
          provinceId: formData.provinceId,
          capacityTonsId: vehicle.capacityTonsId,
          shippingDate: formData.shippingDate
        }),
        formData.districtId 
          ? rm2FeeApi.calculateRM2Fee(formData.districtId, vehicle.capacityTonsId)
          : Promise.resolve(null),
        preferredDriver && formData.shippingDate
          ? driverApi.getDriverSalaryRate(preferredDriver.id, formData.shippingDate)
          : Promise.resolve(null)
      ]);

      // Extract values from settled promises
      const loadUnloadFee = loadUnloadResponse.status === 'fulfilled' ? loadUnloadResponse.value.data.fee : 0;
      
      let tollFee = 0;
      if (tollFeesResponse.status === 'fulfilled') {
        const tollFees = tollFeesResponse.value.data;
        let found: any = null;
        if (formData.districtId) {
          found = tollFees.find((t: any) => t.capacityTonsId === vehicle.capacityTonsId && t.districtId === formData.districtId);
          if (!found && formData.provinceId) {
            found = tollFees.find((t: any) => t.capacityTonsId === vehicle.capacityTonsId && t.provinceId === formData.provinceId && !t.districtId);
          }
        } else if (formData.provinceId) {
          found = tollFees.find((t: any) => t.capacityTonsId === vehicle.capacityTonsId && t.provinceId === formData.provinceId && !t.districtId);
        }
        if (found) tollFee = found.fee;
      }

      // Get shipping cost from capacity-based API response
      let shippingCost = 0;
      if (shippingPriceResponse.status === 'fulfilled' && shippingPriceResponse.value) {
        const responseData = shippingPriceResponse.value.data;
        if (typeof responseData === 'number') {
          shippingCost = responseData;
        }
      }

      let rm2Fee = 0;
      if (rm2FeeResponse.status === 'fulfilled' && rm2FeeResponse.value) {
        if (rm2FeeResponse.value.data.applicable) {
          rm2Fee = rm2FeeResponse.value.data.fee;
        }
      }

      let salaryRate = formData.salaryRate;
      if (salaryRateResponse.status === 'fulfilled' && salaryRateResponse.value) {
        salaryRate = salaryRateResponse.value.data.salaryRate;
      }

      // Update all fields at once
      setFormData(prev => ({
        ...prev,
        driverId: preferredDriver?.id || prev.driverId,
        driverName: preferredDriver?.name || prev.driverName,
        driverPhone: preferredDriver?.phone || prev.driverPhone,
        salaryRate,
        loadUnloadFee,
        tollFee,
        shippingCost,
        rm2Fee
      }));

    } catch (error) {
      console.error('Error calculating all costs:', error);
      alert('Failed to calculate costs. Please try again.');
    }
  }, [formData.licensePlate, formData.provinceId, formData.districtId, formData.shippingDate, formData.salaryRate, vehicles, drivers, setFormData]);

  const updateLoadUnloadFee = useCallback(async () => {
    const vehicle = vehicles.find(v => v.licensePlate && v.licensePlate.toLowerCase() === formData.licensePlate.toLowerCase());
    if (vehicle?.capacityTonsId) {
      try {
        const response = await loadUnloadFeeApi.getByCapacity(vehicle.capacityTonsId);
        setFormData(prev => ({ ...prev, loadUnloadFee: response.data.fee }));
      } catch (error) {
        console.error('Error updating load/unload fee:', error);
      }
    }
  }, [formData.licensePlate, vehicles, setFormData]);

  const updateTollFee = useCallback(async () => {
    const vehicle = vehicles.find(v => v.licensePlate && v.licensePlate.toLowerCase() === formData.licensePlate.toLowerCase());
    if (vehicle?.capacityTonsId) {
      try {
        const tollFees = await tollFeeApi.getAll();
        let found: any = null;
        if (formData.districtId) {
          found = tollFees.data.find((t: any) => t.capacityTonsId === vehicle.capacityTonsId && t.districtId === formData.districtId);
          if (!found && formData.provinceId) {
            found = tollFees.data.find((t: any) => t.capacityTonsId === vehicle.capacityTonsId && t.provinceId === formData.provinceId && !t.districtId);
          }
        } else if (formData.provinceId) {
          found = tollFees.data.find((t: any) => t.capacityTonsId === vehicle.capacityTonsId && t.provinceId === formData.provinceId && !t.districtId);
        }
        const tollFee = found ? found.fee : 0;
        setFormData(prev => ({ ...prev, tollFee }));
      } catch (error) {
        console.error('Error updating toll fee:', error);
      }
    }
  }, [formData.licensePlate, formData.provinceId, formData.districtId, vehicles, setFormData]);

  const updateShippingPrice = useCallback(async () => {
    const vehicle = vehicles.find(v => v.licensePlate && v.licensePlate.toLowerCase() === formData.licensePlate.toLowerCase());
    if (!vehicle?.capacityTonsId) return;
    
    try {
      // Use capacity-based pricing API
      const response = await shippingPriceByCapacityApi.calculatePrice({
        districtId: formData.districtId,
        provinceId: formData.provinceId,
        capacityTonsId: vehicle.capacityTonsId,
        shippingDate: formData.shippingDate
      });
      setFormData(prev => ({ ...prev, shippingCost: response.data }));
    } catch (error) {
      console.error('Error updating shipping price:', error);
    }
  }, [formData.districtId, formData.provinceId, formData.licensePlate, formData.shippingDate, vehicles, setFormData]);

  const updateRM2Fee = useCallback(async () => {
    try {
      const vehicle = vehicles.find(v => v.id === formData.vehicleId);
      if (formData.districtId && vehicle?.capacityTonsId) {
        try {
          const response = await rm2FeeApi.calculateRM2Fee(formData.districtId, vehicle.capacityTonsId);
          if (response.data.applicable) {
            setFormData(prev => ({ ...prev, rm2Fee: response.data.fee }));
          } else {
            setFormData(prev => ({ ...prev, rm2Fee: 0 }));
          }
        } catch (error) {
          console.error('Error updating RM2 fee:', error);
          setFormData(prev => ({ ...prev, rm2Fee: 0 }));
        }
      } else {
        setFormData(prev => ({ ...prev, rm2Fee: 0 }));
      }
    } catch (error) {
      console.error('Error updating RM2 fee:', error);
    }
  }, [formData.districtId, formData.vehicleId, vehicles, setFormData]);

  const updateSalaryRate = useCallback(async () => {
    if (formData.driverId && formData.shippingDate) {
      try {
        const response = await driverApi.getDriverSalaryRate(formData.driverId, formData.shippingDate);
        if (response.data && response.data.salaryRate !== undefined) {
          setFormData(prev => ({ ...prev, salaryRate: response.data.salaryRate }));
        }
      } catch (error) {
        console.error('Error updating salary rate:', error);
      }
    }
  }, [formData.driverId, formData.shippingDate, setFormData]);

  // Auto-calculate fees when license plate or location changes
  useEffect(() => {
    if (!isEditMode && formData.licensePlate && vehicles.length > 0) {
      calculateFees(formData.licensePlate, formData.provinceId, formData.districtId);
    }
  }, [isEditMode, formData.licensePlate, formData.provinceId, formData.districtId, vehicles, calculateFees]);

  // Auto-calculate RM2Fee when district or vehicle changes
  useEffect(() => {
    const calculateRM2Fee = async () => {
      try {
        if (formData.districtId) {
          const vehicle = vehicles.find(v => v.id === formData.vehicleId);
          const response = await rm2FeeApi.calculateRM2Fee(formData.districtId, vehicle?.capacityTonsId);
          if (response.data.applicable) {
            setFormData(prev => ({ ...prev, rm2Fee: response.data.fee }));
          } else {
            setFormData(prev => ({ ...prev, rm2Fee: 0 }));
          }
        } else {
          setFormData(prev => ({ ...prev, rm2Fee: 0 }));
        }
      } catch (error) {
        console.error('Error calculating RM2 fee:', error);
        setFormData(prev => ({ ...prev, rm2Fee: 0 }));
      }
    };
    if (!isEditMode)
      calculateRM2Fee();
  }, [isEditMode, formData.districtId, formData.vehicleId, vehicles, setFormData]);

  return {
    calculateAllCosts,
    updateLoadUnloadFee,
    updateTollFee,
    updateShippingPrice,
    updateRM2Fee,
    updateSalaryRate
  };
};
